import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ClipboardCheck,
  Plus,
  Search,
  Filter,
  Calendar,
  User,
  Building2,
  MapPin,
  AlertTriangle,
  CheckCircle,
  Clock,
  XCircle,
  ArrowLeft,
  ChevronDown,
  BarChart3,
  FileText,
  Eye,
  Trash2,
  Radio,
  TrendingUp,
  AlertCircle,
  FileDown
} from 'lucide-react';
import { api } from '@/utils/api';
import { toCsv, downloadCsv, type CsvColumn } from '@/utils/csvExport';

interface SafetyAudit {
  id: number;
  auditId: string;
  program: string;
  auditType: string;
  siteName: string;
  siteId: string;
  siteAddress: string;
  city: string;
  state: string;
  region: string;
  market: string;
  carrierName: string;
  contractorName: string;
  auditorName: string;
  startDate: string;
  status: string;
  scorePercentage: number | null;
  passFailStatus: string | null;
  findings: { id: number; severity: string; status: string }[];
}

interface Filters {
  search: string;
  auditorName: string;
  contractorName: string;
  carrierName: string;
  siteId: string;
  region: string;
  market: string;
  status: string;
  startDateFrom: string;
  startDateTo: string;
  // S2 (AUDIT-INCIDENT-HISTORY PRD v1): remaining schema dimensions + findings filters.
  constructionSupervisor: string;
  siteName: string;
  client: string;
  division: string;
  auditType: string;
  program: string;
  towerType: string;
  civilOrTower: string;
  city: string;
  state: string;
  findingType: string;
  severity: string;
}

// S2: label/placeholder map for the added text filters (rendered in the panel grid).
const S2_TEXT_FILTERS: Array<[keyof Filters, string, string]> = [
  ['constructionSupervisor', 'Foreman / Supervisor', 'Supervisor name...'],
  ['siteName', 'Site name', 'Site name...'],
  ['client', 'Client', 'Client name...'],
  ['division', 'Division', 'Services, Civil...'],
  ['auditType', 'Audit type', 'Site, Virtual...'],
  ['program', 'Program', 'Program name...'],
  ['towerType', 'Tower type', 'Monopole, Guyed...'],
  ['civilOrTower', 'Civil / Tower', 'Civil or Tower...'],
  ['city', 'City', 'City...'],
  ['state', 'State', 'IN, OK...'],
  ['findingType', 'Audits with finding type', 'PPE, Documentation...'],
];

const STATUS_STYLES: Record<string, { bg: string; text: string; icon: any }> = {
  DRAFT: { bg: 'bg-gray-100', text: 'text-gray-700', icon: FileText },
  IN_PROGRESS: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Clock },
  PENDING_REVIEW: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: AlertCircle },
  COMPLETED: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
  CLOSED: { bg: 'bg-slate-100', text: 'text-slate-700', icon: CheckCircle },
};

const PASS_FAIL_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  Pass: { bg: 'bg-green-600', text: 'text-white', label: 'Satisfactory' },
  'Conditional Pass': { bg: 'bg-yellow-600', text: 'text-white', label: 'Needs Attention' },
  Fail: { bg: 'bg-red-600', text: 'text-white', label: 'Action Required' },
};

export function SafetyAuditsPage() {
  const navigate = useNavigate();
  // S4: drill-down seeding — the History & Reports trend cards deep-link into
  // this table with ?severity=&findingType=&contractorName=&siteName=&
  // towerType=&constructionSupervisor= (only params the filter panel accepts;
  // unknown params are ignored — no fabricated filters).
  const [searchParams] = useSearchParams();
  const seedFiltersFromParams = (): Partial<Filters> => {
    const allowed: Array<keyof Filters> = [
      'severity', 'findingType', 'contractorName', 'siteName', 'towerType', 'constructionSupervisor',
      // Sol-3 (contract F3): trend-card drill links carry the card's period —
      // the seeded UTC days must reach the list API call.
      'startDateFrom', 'startDateTo',
    ];
    const seeded: Partial<Filters> = {};
    allowed.forEach((key) => {
      const v = searchParams.get(key);
      if (v) (seeded as Record<string, string>)[key] = v;
    });
    return seeded;
  };
  // D-Sol-1d: no client role gate — the page's own list fetch decides. A 403
  // from the server renders the why+how restriction panel in place of the
  // table (never a silent redirect and never a client role guess).
  // Mutation gate mirrors the server's requireRole(...MANAGEMENT_ROLES,
  // 'SUPER_ADMIN') — fail-closed for unknown roles so non-mutation roles
  // never see affordances that will 403.
  const [canMutate] = useState<boolean>(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || 'null');
      return ['ADMIN', 'SAFETY_MANAGER', 'PROJECT_MANAGER', 'CONSTRUCTION_MANAGER', 'CONSTRUCTION_SUPERVISOR', 'SUPER_ADMIN'].includes(user?.role);
    } catch { return false; }
  });
  const [audits, setAudits] = useState<SafetyAudit[]>([]);
  const [loading, setLoading] = useState(true);
  // S2: a failed load must be visible — never present stale rows as the
  // current result.
  const [loadError, setLoadError] = useState<string | null>(null);
  // QA-001: request-generation guard — every loadAudits call takes a ticket;
  // only the call holding the LATEST ticket may commit state. A slower
  // earlier request (rows, pagination, error banner, loading) must never
  // overwrite a newer one's result.
  const loadGenerationRef = useRef(0);
  // Analytics loads are ordered/invalidated independently of loadAudits:
  // bumping this in the mount cleanup marks an in-flight analytics response
  // as stale so its setAnalytics commit (and any late settle) is dropped.
  const analyticsGenerationRef = useRef(0);
  // r4 QA fix: post-unmount continuations (e.g. handleDelete's reload) start
  // NEW requests with fresh generations that pass the generation guard —
  // gate those with an unmounted flag, reset at effect start so React dev
  // StrictMode's effect -> cleanup -> effect double-run still loads.
  const unmountedRef = useRef(false);
  // Sol-4: filters the CURRENT rows were loaded with. Export CSV stays
  // disabled while the edit-state filters differ from them (the loaded rows
  // are not the answer to the edited query), while a request is in flight,
  // and while no rows are loaded.
  const loadedFiltersRef = useRef<Filters | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  
  const [filters, setFilters] = useState<Filters>({
    search: '',
    auditorName: '',
    contractorName: '',
    carrierName: '',
    siteId: '',
    region: '',
    market: '',
    status: '',
    startDateFrom: '',
    startDateTo: '',
    constructionSupervisor: '',
    siteName: '',
    client: '',
    division: '',
    auditType: '',
    program: '',
    towerType: '',
    civilOrTower: '',
    city: '',
    state: '',
    findingType: '',
    severity: '',
    ...seedFiltersFromParams(),
  });

  // Analytics summary
  const [analytics, setAnalytics] = useState({
    totalAudits: 0,
    completedAudits: 0,
    avgScore: 0,
    openFindings: 0,
  });
  // Sol-5 (contract F5): analytics failures must never read as data. The
  // summary cards render ONLY on a settled success; on failure the numeric
  // cards are replaced by a role=alert why+how message — never silent zeros.
  const [analyticsStatus, setAnalyticsStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    unmountedRef.current = false; // StrictMode: second effect run must load.
    loadAudits();
    loadAnalytics();
    // Unmount invalidation: a response that settles after unmount must not
    // commit state. Bumping the load generation marks every in-flight
    // loadAudits' settle path (success, catch, and finally) as stale, and
    // bumping the analytics generation gates loadAnalytics' commits.
    return () => {
      loadGenerationRef.current += 1;
      analyticsGenerationRef.current += 1;
      unmountedRef.current = true;
    };
  }, []);

  // S2: loadAudits takes the filter set to send as an optional override so
  // callers (clearFilters) can reload with a JUST-CLEARED set — reading
  // `filters` from the closure would send the old, still-unrendered state.
  const loadAudits = async (page = 1, override?: Filters) => {
    // Increment SYNCHRONOUSLY at call start so concurrent invocations are
    // ordered by generation, not by resolution time.
    if (unmountedRef.current) return; // r4: post-unmount request starts are gated.
    const generation = ++loadGenerationRef.current;
    const active = override ?? filters;
    setLoading(true);
    // Sol-4 (contract F4): a list request starting means the previously
    // loaded rows are NOT the answer to the in-flight query — clear them
    // (same clear-to-loading rule as IncidentHistoryPage) so stale rows can
    // never be exported or read under new filters.
    setAudits([]);
    setPagination({ page: 1, totalPages: 1, total: 0 });
    setLoadError(null);
    loadedFiltersRef.current = active;
    try {
      const params = new URLSearchParams({ page: String(page), limit: '20' });

      Object.entries(active).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await api.get(`/safety-audits?${params}`);
      // Stale response: a newer loadAudits is authoritative — drop this one.
      if (generation !== loadGenerationRef.current) return;
      setAudits(response.data.data);
      setPagination({
        page: response.data.pagination.page,
        totalPages: response.data.pagination.totalPages,
        total: response.data.pagination.total,
      });
      setLoadError(null);
    } catch (error: any) {
      console.error('Error loading audits:', error);
      // Stale FAILURE: it must neither clear the newer success's rows nor
      // show a phantom error banner.
      if (generation !== loadGenerationRef.current) return;
      // D-Sol-1c: a 403 is the SERVER's decision — explain why and how
      // (the job packet is the crew's home surface), never a silent blank.
      if (error?.response?.status === 403) {
        const serverMessage = error?.response?.data?.message || error?.response?.data?.error;
        setLoadError(
          `Access denied — the server restricted this view for your role${serverMessage ? ` (${serverMessage})` : ''}. Your job packet (/my-jobs) is your home surface; ask a manager to grant you access.`
        );
        setAudits([]);
        setPagination({ page: 1, totalPages: 1, total: 0 });
        return;
      }
      // Say WHY (server message when present) and HOW to fix it, and drop the
      // stale rows — a failed request's previous results are not the answer.
      const serverMessage = error?.response?.data?.message;
      setLoadError(
        typeof serverMessage === 'string' && serverMessage
          ? serverMessage
          : "Couldn't load audits — check your connection and try again."
      );
      setAudits([]);
      setPagination({ page: 1, totalPages: 1, total: 0 });
    } finally {
      // Only the latest generation may turn the spinner off; an earlier
      // request settling must not end a newer in-flight load.
      if (generation === loadGenerationRef.current) setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    if (unmountedRef.current) return; // r4: post-unmount request starts are gated.
    const generation = ++analyticsGenerationRef.current;
    try {
      const response = await api.get('/safety-audits/analytics');
      // LIVE-UAT G4 fix: the server puts the analytics object at data TOP LEVEL —
      // { data: { totalAudits, completedAudits, avgScore, openFindings, ... } }.
      // The old `?.summary` wrapper existed only in test mocks (mock-mismatch):
      // production cards read zeros forever. Read the real shape directly.
      const a = response.data.data ?? {};
      // Stale commit: the component unmounted (cleanup bumped the generation)
      // — drop this response instead of setting state.
      if (generation !== analyticsGenerationRef.current) return;
      setAnalytics({
        totalAudits: Number(a.totalAudits) || 0,
        completedAudits: Number(a.completedAudits) || 0,
        avgScore: Number(a.avgScore) || 0,
        openFindings: Number(a.openFindings) || 0,
      });
      setAnalyticsStatus('ready');
    } catch (error) {
      console.error('Error loading analytics:', error);
      // Never present initialized zeros as operational data.
      if (generation === analyticsGenerationRef.current) setAnalyticsStatus('error');
    }
  };

  const handleSearch = () => {
    loadAudits(1);
  };

  const clearFilters = () => {
    // S2: clearing must RELOAD with the cleared set — the override dodges the
    // stale-closure trap (setFilters alone would leave stale filtered rows
    // under now-empty controls).
    const cleared: Filters = {
      search: '',
      auditorName: '',
      contractorName: '',
      carrierName: '',
      siteId: '',
      region: '',
      market: '',
      status: '',
      startDateFrom: '',
      startDateTo: '',
      constructionSupervisor: '',
      siteName: '',
      client: '',
      division: '',
      auditType: '',
      program: '',
      towerType: '',
      civilOrTower: '',
      city: '',
      state: '',
      findingType: '',
      severity: '',
    };
    setFilters(cleared);
    loadAudits(1, cleared);
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this audit?')) return;
    try {
      await api.delete(`/safety-audits/${id}`);
      loadAudits(pagination.page);
    } catch (error) {
      console.error('Error deleting audit:', error);
    }
  };

  const getFindingsCount = (audit: SafetyAudit, severity?: string) => {
    if (!audit.findings) return 0;
    if (severity) {
      return audit.findings.filter(f => f.severity === severity).length;
    }
    return audit.findings.length;
  };

  const getOpenFindingsCount = (audit: SafetyAudit) => {
    if (!audit.findings) return 0;
    return audit.findings.filter(f => f.status === 'OPEN' || f.status === 'IN_PROGRESS').length;
  };

  // S4: CSV export of the currently LOADED rows only (client-side) — the
  // button title states that honesty. Date-only field renders as a UTC
  // calendar day (S2 lesson). Columns are what the row objects actually carry.
  const AUDIT_CSV_COLUMNS: Array<CsvColumn<SafetyAudit & Record<string, unknown>>> = [
    { key: 'auditId', label: 'Audit ID' },
    { key: 'startDate', label: 'Start date (UTC day)' },
    { key: 'status', label: 'Status' },
    { key: 'auditType', label: 'Audit type' },
    { key: 'program', label: 'Program' },
    { key: 'siteName', label: 'Site name' },
    { key: 'siteId', label: 'Site ID' },
    { key: 'siteAddress', label: 'Site address' },
    { key: 'city', label: 'City' },
    { key: 'state', label: 'State' },
    { key: 'region', label: 'Region' },
    { key: 'market', label: 'Market' },
    { key: 'carrierName', label: 'Carrier' },
    { key: 'contractorName', label: 'Contractor' },
    { key: 'auditorName', label: 'Auditor' },
    { key: 'constructionSupervisor', label: 'Foreman / Supervisor' },
    { key: 'towerType', label: 'Tower type' },
    { key: 'client', label: 'Client' },
    { key: 'scorePercentage', label: 'Score %' },
    { key: 'passFailStatus', label: 'Pass / Fail' },
    { key: 'findingsTotal', label: 'Findings' },
    { key: 'openFindings', label: 'Open findings' },
  ];
  const handleExportCsv = () => {
    const rows = audits.map((a) => ({
      ...a,
      startDate: a.startDate ? new Date(a.startDate).toISOString().slice(0, 10) : null,
      findingsTotal: (a.findings ?? []).length,
      openFindings: getOpenFindingsCount(a),
    })) as unknown as Array<SafetyAudit & Record<string, unknown>>;
    downloadCsv('safety-audits.csv', toCsv(AUDIT_CSV_COLUMNS, rows));
  };

  // Sol-4 (contract F4): the CSV must only ever contain rows that ARE the
  // answer to the visible filters — disabled while loading, while nothing is
  // loaded, and while the edited filters differ from the loaded query.
  const filtersMatchLoaded =
    !!loadedFiltersRef.current && JSON.stringify(filters) === JSON.stringify(loadedFiltersRef.current);
  const exportEnabled = !loading && audits.length > 0 && filtersMatchLoaded;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="bg-slate-800/50 border-b border-slate-700">
        <div className="container mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                <ClipboardCheck className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Safety Audits</h1>
                <p className="text-slate-400">Health & Safety Inspections</p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Link
                to="/history"
                className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition flex items-center gap-2"
              >
                History &amp; Reports
              </Link>
              <button
                onClick={handleExportCsv}
                disabled={!exportEnabled}
                title="Exports the currently loaded rows"
                aria-label="Export the currently loaded audit rows as CSV"
                className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition flex items-center gap-2"
              >
                <FileDown className="h-4 w-4" />
                Export CSV
              </button>
              <Link
                to="/incidents"
                className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition flex items-center gap-2"
              >
                Incident history
              </Link>
              <Link
                to="/dashboard"
                className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Dashboard
              </Link>
              <Link
                to="/safety-audits/analytics"
                className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition flex items-center gap-2"
              >
                <BarChart3 className="h-4 w-4" />
                Analytics
              </Link>
              <Link
                to="/safety-audits/templates"
                className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition flex items-center gap-2"
              >
                <FileText className="h-4 w-4" />
                Templates
              </Link>
              {canMutate && (
                <Link
                  to="/safety-audits/create"
                  className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg hover:from-emerald-600 hover:to-teal-700 transition flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  New Audit
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Analytics Cards — rendered ONLY when the summary actually loaded
          (Sol-5): a failure hides the numbers and explains why+how, so
          initialized zeros are never presented as operational data. */}
      <div className="container mx-auto px-6 py-6">
        {analyticsStatus === 'error' ? (
          <div role="alert" className="mb-6 p-4 rounded-lg bg-red-900/40 border border-red-700 text-red-200 text-sm max-w-2xl">
            Couldn't load the analytics summary — the server request failed. Refresh the page to retry; if it keeps
            failing, tell your administrator. The audit list below is unaffected.
          </div>
        ) : analyticsStatus === 'ready' ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Total Audits</p>
                <p className="text-2xl font-bold text-white">{analytics.totalAudits}</p>
              </div>
              <ClipboardCheck className="h-8 w-8 text-emerald-500" />
            </div>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Completed</p>
                <p className="text-2xl font-bold text-white">{analytics.completedAudits}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Avg Score</p>
                <p className="text-2xl font-bold text-white">{(analytics.avgScore || 0).toFixed(1)}%</p>
              </div>
              <TrendingUp className="h-8 w-8 text-blue-500" />
            </div>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Open Findings</p>
                <p className="text-2xl font-bold text-white">{analytics.openFindings}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-amber-500" />
            </div>
          </div>
        </div>
        ) : null}

        {/* Search & Filters */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[300px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by audit ID, site name, address..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 transition ${
                showFilters ? 'bg-emerald-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              <Filter className="h-4 w-4" />
              Filters
              <ChevronDown className={`h-4 w-4 transition ${showFilters ? 'rotate-180' : ''}`} />
            </button>
            <button
              onClick={handleSearch}
              className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-500 transition"
            >
              Search
            </button>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-slate-700 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Auditor</label>
                <input
                  type="text"
                  value={filters.auditorName}
                  onChange={(e) => setFilters(prev => ({ ...prev, auditorName: e.target.value }))}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                  placeholder="Auditor name..."
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Contractor</label>
                <input
                  type="text"
                  value={filters.contractorName}
                  onChange={(e) => setFilters(prev => ({ ...prev, contractorName: e.target.value }))}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                  placeholder="Contractor name..."
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Carrier</label>
                <input
                  type="text"
                  value={filters.carrierName}
                  onChange={(e) => setFilters(prev => ({ ...prev, carrierName: e.target.value }))}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                  placeholder="Carrier name..."
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Site ID</label>
                <input
                  type="text"
                  value={filters.siteId}
                  onChange={(e) => setFilters(prev => ({ ...prev, siteId: e.target.value }))}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                  placeholder="Site ID..."
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Region</label>
                <input
                  type="text"
                  value={filters.region}
                  onChange={(e) => setFilters(prev => ({ ...prev, region: e.target.value }))}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                  placeholder="Region..."
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Market</label>
                <input
                  type="text"
                  value={filters.market}
                  onChange={(e) => setFilters(prev => ({ ...prev, market: e.target.value }))}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                  placeholder="Market..."
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                >
                  <option value="">All Statuses</option>
                  <option value="DRAFT">Draft</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="PENDING_REVIEW">Pending Review</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="CLOSED">Closed</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Date Range</label>
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={filters.startDateFrom}
                    onChange={(e) => setFilters(prev => ({ ...prev, startDateFrom: e.target.value }))}
                    className="flex-1 px-2 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                  />
                  <input
                    type="date"
                    value={filters.startDateTo}
                    onChange={(e) => setFilters(prev => ({ ...prev, startDateTo: e.target.value }))}
                    className="flex-1 px-2 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                  />
                </div>
              </div>
              {/* S2 (AUDIT-INCIDENT-HISTORY PRD v1): remaining schema dimensions */}
              {S2_TEXT_FILTERS.map(([key, label, placeholder]) => (
                <div key={key}>
                  <label className="block text-sm text-slate-400 mb-1">{label}</label>
                  <input
                    type="text"
                    value={filters[key]}
                    onChange={(e) => setFilters(prev => ({ ...prev, [key]: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                    placeholder={placeholder}
                  />
                </div>
              ))}
              <div>
                <label className="block text-sm text-slate-400 mb-1">Audits with finding severity</label>
                <select
                  value={filters.severity}
                  onChange={(e) => setFilters(prev => ({ ...prev, severity: e.target.value }))}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                >
                  <option value="">All Severities</option>
                  <option value="OBSERVATION">Observation</option>
                  <option value="MINOR">Minor</option>
                  <option value="MAJOR">Major</option>
                  <option value="CRITICAL">Critical</option>
                  <option value="STOP_WORK">Stop Work</option>
                </select>
              </div>
              <div className="col-span-full flex justify-end">
                <button
                  onClick={clearFilters}
                  className="text-slate-400 hover:text-white text-sm"
                >
                  Clear All Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Results */}
        {loadError && (
          <div role="alert" className="container mx-auto px-6">
            <div className="mb-4 flex items-start gap-3 rounded-lg border border-red-700 bg-red-900/40 px-4 py-3 text-red-200">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
              <div>
                <p className="font-semibold">Couldn't load audits</p>
                <p className="text-sm">{loadError}</p>
              </div>
            </div>
          </div>
        )}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
          <div className="p-4 border-b border-slate-700 flex items-center justify-between">
            <span className="text-slate-400">
              {pagination.total} audit{pagination.total !== 1 ? 's' : ''} found
            </span>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <Clock className="h-12 w-12 text-slate-500 mx-auto mb-4 animate-pulse" />
              <p className="text-slate-400">Loading audits...</p>
            </div>
          ) : audits.length === 0 ? (
            <div className="p-12 text-center">
              <ClipboardCheck className="h-12 w-12 text-slate-500 mx-auto mb-4" />
              <p className="text-slate-400">No audits found</p>
              {canMutate && (
                <Link
                  to="/safety-audits/create"
                  className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-500"
                >
                  <Plus className="h-4 w-4" />
                  Create First Audit
                </Link>
              )}
            </div>
          ) : (
            <div className="divide-y divide-slate-700">
              {audits.map((audit) => {
                const statusStyle = STATUS_STYLES[audit.status] || STATUS_STYLES.DRAFT;
                const StatusIcon = statusStyle.icon;
                const passFailStyle = audit.passFailStatus ? PASS_FAIL_STYLES[audit.passFailStatus] : null;

                return (
                  <div
                    key={audit.id}
                    className="p-4 hover:bg-slate-700/30 transition cursor-pointer"
                    onClick={() => navigate(`/safety-audits/${audit.id}`)}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-lg font-semibold text-white">{audit.auditId}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text} flex items-center gap-1`}>
                            <StatusIcon className="h-3 w-3" />
                            {audit.status.replace('_', ' ')}
                          </span>
                          {passFailStyle && (
                            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${passFailStyle.bg} ${passFailStyle.text}`}>
                              {passFailStyle.label}
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div className="flex items-center gap-2 text-slate-400">
                            <MapPin className="h-4 w-4" />
                            <span>{audit.siteName || audit.siteAddress || 'No Site'}</span>
                          </div>
                          <div className="flex items-center gap-2 text-slate-400">
                            <Radio className="h-4 w-4" />
                            <span>{audit.carrierName || 'N/A'}</span>
                          </div>
                          <div className="flex items-center gap-2 text-slate-400">
                            <User className="h-4 w-4" />
                            <span>{audit.auditorName}</span>
                          </div>
                          <div className="flex items-center gap-2 text-slate-400">
                            <Calendar className="h-4 w-4" />
                            <span>{new Date(audit.startDate).toLocaleDateString(undefined, { timeZone: 'UTC' })}</span>
                          </div>
                        </div>
                        {audit.contractorName && (
                          <div className="mt-2 flex items-center gap-2 text-sm text-slate-500">
                            <Building2 className="h-4 w-4" />
                            <span>Contractor: {audit.contractorName}</span>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col items-end gap-2">
                        {audit.scorePercentage != null && (
                          <div className="text-right">
                            <span className="text-2xl font-bold text-white">{Number(audit.scorePercentage).toFixed(0)}%</span>
                            <p className="text-xs text-slate-400">Score</p>
                          </div>
                        )}
                        {getFindingsCount(audit) > 0 && (
                          <div className="flex items-center gap-2">
                            {getFindingsCount(audit, 'STOP_WORK') > 0 && (
                              <span className="px-2 py-1 bg-purple-900/50 text-purple-400 rounded text-xs font-medium">
                                {getFindingsCount(audit, 'STOP_WORK')} Stop Work
                              </span>
                            )}
                            {getFindingsCount(audit, 'CRITICAL') > 0 && (
                              <span className="px-2 py-1 bg-red-900/50 text-red-400 rounded text-xs font-medium">
                                {getFindingsCount(audit, 'CRITICAL')} Critical
                              </span>
                            )}
                            {getFindingsCount(audit, 'MAJOR') > 0 && (
                              <span className="px-2 py-1 bg-orange-900/50 text-orange-400 rounded text-xs font-medium">
                                {getFindingsCount(audit, 'MAJOR')} Major
                              </span>
                            )}
                            {getFindingsCount(audit, 'MINOR') > 0 && (
                              <span className="px-2 py-1 bg-yellow-900/50 text-yellow-400 rounded text-xs font-medium">
                                {getFindingsCount(audit, 'MINOR')} Minor
                              </span>
                            )}
                            {getFindingsCount(audit, 'OBSERVATION') > 0 && (
                              <span className="px-2 py-1 bg-blue-900/50 text-blue-400 rounded text-xs font-medium">
                                {getFindingsCount(audit, 'OBSERVATION')} Observation
                              </span>
                            )}
                            {getOpenFindingsCount(audit) > 0 && (
                              <span className="px-2 py-1 bg-amber-900/50 text-amber-400 rounded text-xs font-medium">
                                {getOpenFindingsCount(audit)} Open
                              </span>
                            )}
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <button
                            aria-label={`View audit ${audit.auditId}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/safety-audits/${audit.id}`);
                            }}
                            className="p-2 text-slate-400 hover:text-white hover:bg-slate-600 rounded-lg transition"
                          >
                            <Eye aria-hidden="true" className="h-4 w-4" />
                          </button>
                          {canMutate && (
                            <button
                              aria-label={`Delete audit ${audit.auditId}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(audit.id);
                              }}
                              className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-900/30 rounded-lg transition"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="p-4 border-t border-slate-700 flex items-center justify-center gap-2">
              <button
                onClick={() => loadAudits(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="px-3 py-1 bg-slate-700 text-white rounded disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-slate-400">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => loadAudits(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
                className="px-3 py-1 bg-slate-700 text-white rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

